import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Globe, Eye, Share2, Settings, Plus, Copy, Check, Trash2, Mail, AlertTriangle, Zap, Crown, Grid, List, LayoutGrid, Columns, ArrowUpRight, X, Loader2 } from 'lucide-react';
import { ImageUpload } from '../components/ImageUpload';
import { SafeImage } from '../components/SafeImage';
import api from '../utils/api';
import { toast } from 'sonner';

export const MinisiteDashboard = () => {
  const navigate = useNavigate();
  const [minisite, setMinisite] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [supportEmail, setSupportEmail] = useState('support@downpricer.com');
  
  const [articleForm, setArticleForm] = useState({
    name: '',
    description: '',
    photos: [],
    price: '',
    reference_price: '',
    platform_links: { vinted: '', leboncoin: '' },
    show_in_reseller_catalog: false
  });

  const [settingsForm, setSettingsForm] = useState({
    site_name: '',
    logo_url: '',
    welcome_text: '',
    template: 'modern-grid',
    primary_color: '#FF5722',
    font_family: 'Arial'
  });

  // Templates avec descriptions et preview icons
  const ALL_TEMPLATES = [
    { id: 'modern-grid', name: 'Grille Moderne', desc: 'Style Amazon/E-commerce', icon: Grid },
    { id: 'classic-list', name: 'Liste Classique', desc: 'Style YouTube/Reddit', icon: List },
    { id: 'card-stack', name: 'Cards Empilées', desc: 'Style Spotify/Netflix', icon: Columns },
    { id: 'minimal-clean', name: 'Minimaliste', desc: 'Clean avec espaces', icon: LayoutGrid },
    { id: 'bold-hero', name: 'Hero Bold', desc: 'Gros visuels', icon: ArrowUpRight },
    { id: 'elegant-split', name: 'Split Élégant', desc: 'Vue 1/3 - 2/3', icon: Columns },
    { id: 'masonry-flow', name: 'Masonry', desc: 'Style Pinterest', icon: Grid },
    { id: 'side-scroll', name: 'Scroll Horizontal', desc: 'Carrousel', icon: ArrowUpRight },
    { id: 'full-width', name: 'Pleine Largeur', desc: 'Articles géants', icon: LayoutGrid },
    { id: 'compact-tiles', name: 'Tuiles Compactes', desc: 'Dense et rapide', icon: Grid },
    { id: 'magazine-style', name: 'Style Magazine', desc: 'Mise en page édito', icon: Columns },
    { id: 'portfolio-pro', name: 'Portfolio Pro', desc: 'Galerie pro', icon: LayoutGrid },
    { id: 'showcase-xl', name: 'Showcase XL', desc: 'Mise en avant', icon: ArrowUpRight },
    { id: 'gallery-view', name: 'Vue Galerie', desc: 'Photos focus', icon: Grid },
    { id: 'business-card', name: 'Business Card', desc: 'Pro et sobre', icon: List },
    { id: 'storyteller', name: 'Storyteller', desc: 'Narratif', icon: Columns },
    { id: 'product-focus', name: 'Product Focus', desc: 'Détails produit', icon: LayoutGrid },
    { id: 'dark-luxe', name: 'Dark Luxe', desc: 'Premium sombre', icon: Crown },
    { id: 'bright-fresh', name: 'Bright Fresh', desc: 'Léger et aéré', icon: Zap },
    { id: 'premium-elite', name: 'Premium Elite', desc: 'Haut de gamme', icon: Crown }
  ];

  const ALL_FONTS = [
    'Arial', 'Helvetica', 'Georgia', 'Verdana', 'Roboto', 
    'Open Sans', 'Outfit', 'Lato', 'Montserrat', 'Poppins',
    'Inter', 'Nunito', 'Raleway', 'Oswald', 'Playfair Display',
    'Merriweather', 'Source Sans Pro', 'PT Sans', 'Ubuntu', 'Cabin'
  ];

  useEffect(() => {
    fetchMinisiteData();
    fetchSettings();
  }, []);

  const fetchMinisiteData = async () => {
    try {
      const siteRes = await api.get('/minisites/my');
      setMinisite(siteRes.data);
      setSettingsForm({
        site_name: siteRes.data.site_name,
        logo_url: siteRes.data.logo_url || '',
        welcome_text: siteRes.data.welcome_text || '',
        template: siteRes.data.template || 'modern-grid',
        primary_color: siteRes.data.primary_color || '#FF5722',
        font_family: siteRes.data.font_family || 'Arial'
      });
      
      const articlesResponse = await api.get(`/minisites/${siteRes.data.id}/articles`);
      setArticles(articlesResponse.data);
    } catch (error) {
      if (error.response?.status === 404) {
        navigate('/minisite');
      } else {
        toast.error('Erreur lors du chargement');
      }
    }
    setLoading(false);
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      const settings = response.data;
      if (settings.support_email) setSupportEmail(settings.support_email);
      else if (settings.billing_support_email) setSupportEmail(settings.billing_support_email);
      else if (settings.contact_email) setSupportEmail(settings.contact_email);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const getPlanLabel = (planId) => {
    const plans = {
      'SITE_PLAN_1': 'Starter (1€/mois)',
      'SITE_PLAN_10': 'Standard (10€/mois)',
      'SITE_PLAN_15': 'Premium (15€/mois)'
    };
    return plans[planId] || planId;
  };

  const getPlanQuota = (planId) => {
    const quotas = { 'SITE_PLAN_1': 5, 'SITE_PLAN_10': 10, 'SITE_PLAN_15': 20 };
    return quotas[planId] || 5;
  };

  const getPlanFeatures = (planId) => {
    const features = {
      'SITE_PLAN_1': {
        templates: ALL_TEMPLATES.slice(0, 3),
        fonts: ALL_FONTS.slice(0, 3),
        customColors: false,
        canShowInResellerCatalog: false
      },
      'SITE_PLAN_10': {
        templates: ALL_TEMPLATES.slice(0, 10),
        fonts: ALL_FONTS.slice(0, 10),
        customColors: true,
        canShowInResellerCatalog: true
      },
      'SITE_PLAN_15': {
        templates: ALL_TEMPLATES,
        fonts: ALL_FONTS,
        customColors: true,
        canShowInResellerCatalog: true
      }
    };
    return features[planId] || features['SITE_PLAN_1'];
  };

  const handleAddArticle = async () => {
    if (!articleForm.name || !articleForm.price) {
      toast.error('Nom et prix requis');
      return;
    }
    if (!articleForm.platform_links.vinted && !articleForm.platform_links.leboncoin) {
      toast.error('Au moins un lien (Vinted ou Leboncoin) requis');
      return;
    }
    try {
      await api.post(`/minisites/${minisite.id}/articles`, {
        ...articleForm,
        price: parseFloat(articleForm.price),
        reference_price: parseFloat(articleForm.reference_price) || parseFloat(articleForm.price)
      });
      toast.success('Article ajouté');
      setShowArticleModal(false);
      setArticleForm({ name: '', description: '', photos: [], price: '', reference_price: '', platform_links: { vinted: '', leboncoin: '' }, show_in_reseller_catalog: false });
      fetchMinisiteData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await api.delete(`/minisites/${minisite.id}/articles/${articleId}`);
      toast.success('Article supprimé');
      fetchMinisiteData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteMinisite = async () => {
    if (!deleteReason.trim()) {
      toast.error('Veuillez indiquer une raison');
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/minisites/${minisite.id}`, { data: { reason: deleteReason } });
      toast.success('Mini-site supprimé');
      navigate('/minisite');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
    setDeleting(false);
  };

  const handleSaveSettings = async () => {
    try {
      await api.put(`/minisites/${minisite.id}`, settingsForm);
      toast.success('Paramètres mis à jour');
      fetchMinisiteData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(siteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('URL copiée !');
  };

  const handleUpgrade = () => {
    navigate('/minisite/upgrade');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!minisite) return null;

  const siteUrl = `${window.location.origin}/s/${minisite.slug}`;
  const quota = getPlanQuota(minisite.plan_id);
  const features = getPlanFeatures(minisite.plan_id);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-orange-500 flex items-center gap-2">
              <Globe className="h-7 w-7" /> {minisite.site_name}
            </h1>
            <p className="text-zinc-400 text-sm mt-1">/{minisite.slug}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-zinc-800 text-orange-400">{getPlanLabel(minisite.plan_id)}</Badge>
            {minisite.plan_id !== 'SITE_PLAN_15' && (
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={handleUpgrade}>
                <Crown className="h-4 w-4 mr-1" /> Upgrade
              </Button>
            )}
          </div>
        </div>

        <Tabs defaultValue="articles" className="w-full">
          <TabsList className="bg-zinc-900 border-zinc-800 w-full flex overflow-x-auto">
            <TabsTrigger value="articles" className="flex-1 data-[state=active]:bg-orange-500">
              Articles ({articles.length}/{quota})
            </TabsTrigger>
            <TabsTrigger value="share" className="flex-1 data-[state=active]:bg-orange-500">Partage</TabsTrigger>
            <TabsTrigger value="stats" className="flex-1 data-[state=active]:bg-orange-500">Stats</TabsTrigger>
            <TabsTrigger value="appearance" className="flex-1 data-[state=active]:bg-orange-500">Apparence</TabsTrigger>
            <TabsTrigger value="settings" className="flex-1 data-[state=active]:bg-orange-500">Param.</TabsTrigger>
          </TabsList>

          {/* Tab Articles */}
          <TabsContent value="articles" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Mes articles</h2>
              <Button onClick={() => setShowArticleModal(true)} disabled={articles.length >= quota} className="bg-orange-600 hover:bg-orange-700">
                <Plus className="h-4 w-4 mr-2" /> Ajouter un article
              </Button>
            </div>
            
            {articles.length >= quota && (
              <Card className="bg-orange-900/20 border-orange-500/50 mb-4">
                <CardContent className="p-4 flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  <div>
                    <p className="text-orange-400 font-medium">Limite atteinte ({quota} articles max)</p>
                    <p className="text-sm text-zinc-400">Passez au plan supérieur pour ajouter plus d'articles</p>
                  </div>
                  <Button size="sm" className="ml-auto bg-purple-600 hover:bg-purple-700" onClick={handleUpgrade}>
                    <Crown className="h-4 w-4 mr-1" /> Upgrade
                  </Button>
                </CardContent>
              </Card>
            )}

            {articles.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-12 text-center">
                  <p className="text-zinc-400">Aucun article. Ajoutez votre premier article !</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {articles.map((article) => (
                  <Card key={article.id} className="bg-zinc-900 border-zinc-800 overflow-hidden">
                    <div className="h-40 bg-zinc-800">
                      <SafeImage src={article.photos?.[0]} alt={article.name} className="w-full h-full object-cover" />
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-white truncate mb-1">{article.name}</h3>
                      <p className="text-sm text-zinc-400 line-clamp-2 mb-2">{article.description}</p>
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-lg font-bold text-orange-500">{article.price}€</span>
                        {article.reference_price > article.price && (
                          <Badge className="bg-green-600">-{Math.round(((article.reference_price - article.price) / article.reference_price) * 100)}%</Badge>
                        )}
                      </div>
                      <Button size="sm" variant="outline" className="w-full border-red-400 text-red-400 hover:bg-red-900/20" onClick={() => handleDeleteArticle(article.id)}>
                        <Trash2 className="h-4 w-4 mr-1" /> Supprimer
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tab Partage */}
          <TabsContent value="share" className="mt-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader><CardTitle className="text-orange-500">Partager votre mini-site</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">URL publique</Label>
                  <div className="flex gap-2">
                    <Input value={siteUrl} readOnly className="bg-zinc-800 border-zinc-700 text-white" />
                    <Button onClick={handleCopyUrl} className="bg-orange-600 hover:bg-orange-700">
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <Button onClick={() => window.open(siteUrl, '_blank')} className="bg-blue-600 hover:bg-blue-700">
                  <Globe className="h-4 w-4 mr-2" /> Voir mon site
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Stats */}
          <TabsContent value="stats" className="mt-6">
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-6">
                  <p className="text-sm text-orange-400 font-medium mb-1">Vues totales</p>
                  <p className="text-3xl font-bold text-white">{minisite.views || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-6">
                  <p className="text-sm text-orange-400 font-medium mb-1">Articles en ligne</p>
                  <p className="text-3xl font-bold text-white">{articles.length}</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-6">
                  <p className="text-sm text-orange-400 font-medium mb-1">Plan actuel</p>
                  <p className="text-lg font-bold text-white">{getPlanLabel(minisite.plan_id)}</p>
                  {minisite.plan_id !== 'SITE_PLAN_15' && (
                    <Button size="sm" className="mt-2 bg-purple-600 hover:bg-purple-700" onClick={handleUpgrade}>
                      <Crown className="h-4 w-4 mr-1" /> Upgrade
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab Apparence - TEMPLATES EN GRILLE */}
          <TabsContent value="appearance" className="mt-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-orange-500">Choisir un template</CardTitle>
                <p className="text-sm text-zinc-400">{features.templates.length} templates disponibles avec votre plan</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                  {features.templates.map((t) => {
                    const IconComponent = t.icon || Grid;
                    const isSelected = settingsForm.template === t.id;
                    return (
                      <div
                        key={t.id}
                        onClick={() => setSettingsForm({...settingsForm, template: t.id})}
                        className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${
                          isSelected 
                            ? 'border-orange-500 bg-orange-500/10' 
                            : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                        }`}
                      >
                        <div className={`h-16 flex items-center justify-center mb-2 ${isSelected ? 'text-orange-500' : 'text-zinc-400'}`}>
                          <IconComponent className="h-8 w-8" />
                        </div>
                        <h3 className={`font-medium text-sm ${isSelected ? 'text-orange-500' : 'text-white'}`}>{t.name}</h3>
                        <p className="text-xs text-zinc-500">{t.desc}</p>
                        {isSelected && <Badge className="mt-2 bg-orange-600 text-xs">Actif</Badge>}
                      </div>
                    );
                  })}
                </div>

                {features.templates.length < 20 && (
                  <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4 flex items-center gap-3">
                    <Crown className="h-6 w-6 text-purple-500" />
                    <div className="flex-1">
                      <p className="text-purple-400 font-medium">{20 - features.templates.length} templates supplémentaires disponibles</p>
                      <p className="text-xs text-zinc-400">Passez au plan supérieur pour plus de choix</p>
                    </div>
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={handleUpgrade}>Upgrade</Button>
                  </div>
                )}

                <div className="mt-6 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Police ({features.fonts.length} disponibles)</Label>
                    <Select value={settingsForm.font_family} onValueChange={(val) => setSettingsForm({...settingsForm, font_family: val})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700 max-h-60">
                        {features.fonts.map(f => (
                          <SelectItem key={f} value={f} className="text-white hover:bg-zinc-800" style={{fontFamily: f}}>{f}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-300">Couleur principale</Label>
                    {features.customColors ? (
                      <div className="flex gap-2">
                        <Input type="color" value={settingsForm.primary_color} onChange={(e) => setSettingsForm({...settingsForm, primary_color: e.target.value})} className="w-20 h-10" />
                        <Input value={settingsForm.primary_color} onChange={(e) => setSettingsForm({...settingsForm, primary_color: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" />
                      </div>
                    ) : (
                      <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-zinc-500 text-sm">
                        🔒 Couleurs personnalisées disponibles avec le plan Standard ou Premium
                      </div>
                    )}
                  </div>

                  <Button onClick={handleSaveSettings} className="bg-orange-600 hover:bg-orange-700">Enregistrer l'apparence</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Paramètres */}
          <TabsContent value="settings" className="mt-6 space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader><CardTitle className="text-orange-500">Informations du site</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Nom du site</Label>
                  <Input value={settingsForm.site_name} onChange={(e) => setSettingsForm({...settingsForm, site_name: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Logo (URL)</Label>
                  <Input value={settingsForm.logo_url} onChange={(e) => setSettingsForm({...settingsForm, logo_url: e.target.value})} placeholder="https://exemple.com/logo.png" className="bg-zinc-800 border-zinc-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Texte de bienvenue</Label>
                  <Textarea value={settingsForm.welcome_text} onChange={(e) => setSettingsForm({...settingsForm, welcome_text: e.target.value})} rows={3} className="bg-zinc-800 border-zinc-700 text-white" />
                </div>
                <Button onClick={handleSaveSettings} className="bg-orange-600 hover:bg-orange-700">Enregistrer</Button>
              </CardContent>
            </Card>

            {/* Plan & Abonnement */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader><CardTitle className="text-orange-500">Plan & Abonnement</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                  <div>
                    <p className="font-medium text-white">{getPlanLabel(minisite.plan_id)}</p>
                    <p className="text-sm text-zinc-400">{articles.length}/{quota} articles utilisés</p>
                  </div>
                  {minisite.plan_id !== 'SITE_PLAN_15' && (
                    <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleUpgrade}>
                      <Crown className="h-4 w-4 mr-1" /> Upgrade
                    </Button>
                  )}
                </div>

                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-zinc-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-white">Annuler l'abonnement</p>
                      <p className="text-sm text-zinc-400 mb-2">Pour annuler votre abonnement, veuillez contacter notre support :</p>
                      <Button variant="outline" className="border-zinc-600 text-zinc-300" onClick={() => setShowCancelModal(true)}>
                        <Mail className="h-4 w-4 mr-2" /> {supportEmail}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Zone de danger */}
            <Card className="bg-red-900/10 border-red-500/30">
              <CardHeader><CardTitle className="text-red-500">Zone de danger</CardTitle></CardHeader>
              <CardContent>
                <p className="text-zinc-400 text-sm mb-4">
                  La suppression de votre mini-site est irréversible. Tous vos articles seront supprimés et votre abonnement sera annulé.
                </p>
                <Button variant="outline" className="border-red-500 text-red-500 hover:bg-red-500/10" onClick={() => setShowDeleteModal(true)}>
                  <Trash2 className="h-4 w-4 mr-2" /> Supprimer mon mini-site
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Modal Ajouter Article */}
      <Dialog open={showArticleModal} onOpenChange={setShowArticleModal}>
        <DialogContent className="bg-zinc-900 text-white border-zinc-800 max-w-[95vw] md:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-orange-500">Ajouter un article</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <ImageUpload images={articleForm.photos} onChange={(photos) => setArticleForm({...articleForm, photos})} maxImages={5} label="Images de l'article" />
            <div className="space-y-2">
              <Label className="text-zinc-300">Nom de l'article *</Label>
              <Input value={articleForm.name} onChange={(e) => setArticleForm({...articleForm, name: e.target.value})} placeholder="Ex: iPhone 13 128Go" className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Description</Label>
              <Textarea value={articleForm.description} onChange={(e) => setArticleForm({...articleForm, description: e.target.value})} rows={3} className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Prix de vente (€) *</Label>
                <Input type="number" value={articleForm.price} onChange={(e) => setArticleForm({...articleForm, price: e.target.value})} placeholder="50" className="bg-zinc-800 border-zinc-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Prix de référence (€)</Label>
                <Input type="number" value={articleForm.reference_price} onChange={(e) => setArticleForm({...articleForm, reference_price: e.target.value})} placeholder="100" className="bg-zinc-800 border-zinc-700 text-white" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Lien Vinted *</Label>
              <Input value={articleForm.platform_links.vinted} onChange={(e) => setArticleForm({...articleForm, platform_links: {...articleForm.platform_links, vinted: e.target.value}})} placeholder="https://vinted.fr/..." className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Lien Leboncoin</Label>
              <Input value={articleForm.platform_links.leboncoin} onChange={(e) => setArticleForm({...articleForm, platform_links: {...articleForm.platform_links, leboncoin: e.target.value}})} placeholder="https://leboncoin.fr/..." className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            {features.canShowInResellerCatalog && (
              <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer ${articleForm.show_in_reseller_catalog ? 'border-blue-500 bg-blue-500/10' : 'border-zinc-700 bg-zinc-800/50'}`}>
                <input type="checkbox" checked={articleForm.show_in_reseller_catalog} onChange={(e) => setArticleForm({...articleForm, show_in_reseller_catalog: e.target.checked})} className="w-5 h-5 accent-blue-500" />
                <div>
                  <span className="text-sm font-medium text-zinc-300">Afficher dans le catalogue revendeurs</span>
                  <p className="text-xs text-zinc-500">Les autres vendeurs pourront voir cet article</p>
                </div>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArticleModal(false)} className="border-zinc-700 text-white">Annuler</Button>
            <Button onClick={handleAddArticle} className="bg-orange-600 hover:bg-orange-700">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Annuler Abonnement */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="bg-zinc-900 text-white border-zinc-800">
          <DialogHeader><DialogTitle className="text-orange-500">Annuler l'abonnement</DialogTitle></DialogHeader>
          <div className="py-4">
            <div className="bg-zinc-800 p-4 rounded-lg mb-4">
              <p className="text-zinc-300 mb-2">Pour annuler votre abonnement, veuillez nous contacter par email :</p>
              <a href={`mailto:${supportEmail}?subject=Annulation abonnement mini-site&body=Bonjour,%0A%0AJe souhaite annuler mon abonnement mini-site.%0A%0ASlug: ${minisite?.slug}%0APlan: ${getPlanLabel(minisite?.plan_id)}%0A%0ACordialement`} className="text-orange-500 hover:underline font-medium text-lg">
                {supportEmail}
              </a>
            </div>
            <p className="text-sm text-zinc-500">Notre équipe traitera votre demande dans les 24-48h.</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowCancelModal(false)} className="bg-zinc-700 hover:bg-zinc-600">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Supprimer Mini-site */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-zinc-900 text-white border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-red-500 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Supprimer le mini-site
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-red-900/20 border border-red-500/30 p-4 rounded-lg">
              <p className="text-red-400 font-medium mb-2">⚠️ Cette action est irréversible</p>
              <ul className="text-sm text-zinc-400 space-y-1">
                <li>• Tous vos articles seront supprimés</li>
                <li>• Votre URL publique sera désactivée</li>
                <li>• Votre abonnement sera annulé</li>
              </ul>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Raison de la suppression (obligatoire)</Label>
              <Textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} rows={3} placeholder="Indiquez pourquoi vous souhaitez supprimer votre mini-site..." className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="border-zinc-700 text-white">Annuler</Button>
            <Button onClick={handleDeleteMinisite} disabled={!deleteReason.trim() || deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
